import _ from 'lodash';
import { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { Col, Container, Offcanvas, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';

import * as store from '../../../../reducers/store';

import { api } from 'common';

import AgesServedFilter from './Filters/AgesServedFilter';
import LocationsFilter from './Filters/LocationsFilter';

import { isEnumMember } from 'common/dist/utils/types';
import { updateWindowHistory } from '../Common/utils';
import {
  RowWithPadding,
  StyledRow,
  updateFilterParams,
} from './Filters/common';

import { Checkbox, Typography } from '@vivaahealth/design-system';
import { ReactComponent as CloseIcon } from '../../../../assets/modal-close.svg';

interface Props {
  showFilters: boolean;
  closeFilters: () => void;
  filterOptions: api.PrevisitProvider.FilterOptions;
  setFilterOptions: (value: api.PrevisitProvider.FilterOptions) => void;
  setCurrentPage: (value: number) => void;
  supportedLanguages?: api.PrevisitProvider.SupportedLanguageMap;
  supportedLocations?: api.PrevisitProvider.SupportedLocationMap;
  supportedPractices?: api.PrevisitProvider.SupportedPractice[];
  supportedProviderTypes?: api.PrevisitProvider.SupportedProviderTypeMap;
  supportedSexes?: api.PrevisitProvider.SupportedSexMap;
  acceptedPayorWithPlanTypes?: string[];
  ipLocation?: api.IpStack.Response;
}

const Filters: React.FC<Props> = ({
  closeFilters,
  showFilters,
  filterOptions,
  setFilterOptions,
  setCurrentPage,
  supportedLanguages,
  supportedLocations,
  supportedPractices,
  supportedProviderTypes,
  supportedSexes,
  acceptedPayorWithPlanTypes,
  ipLocation,
}) => {
  const urlLocation = useLocation();
  const pathname = urlLocation.pathname;
  const [params, setParams] = useState<URLSearchParams>();
  const [showMoreLanguages, setShowMoreLanguages] = useState(false);
  const embedded = useSelector((store: store.All) => store.embedded);
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );

  useEffect(() => {
    const params = new URLSearchParams(urlLocation.search);
    setParams(params);
  }, [urlLocation, pathname, embedded, organizationId, ipLocation]);

  const handleClose = () => closeFilters();

  const renderPracticeFilter = (
    supportedPractices?: api.PrevisitProvider.SupportedPractice[],
  ) => {
    if (!supportedPractices) {
      return null;
    }
    return (
      <div>
        <RowWithPadding>
          <Typography variant="title-medium" bold className="ps-0">
            Practice name
          </Typography>
        </RowWithPadding>
        {supportedPractices.map((supportedPractice) => {
          const checked = filterOptions.practiceIds?.includes(
            supportedPractice.id,
          );
          return (
            <StyledRow key={supportedPractice.id}>
              <Checkbox
                label={`${supportedPractice.name} (${supportedPractice.count})`}
                small
                checked={checked ?? false}
                onChange={(event) => {
                  const updatedPractices = updateFilterParams({
                    checked: event.target.checked,
                    embedded,
                    filterItem: supportedPractice.id,
                    organizationId,
                    paramString: 'practice',
                    params,
                    pathname,
                  });
                  setFilterOptions({
                    ...filterOptions,
                    practiceIds: updatedPractices,
                  });
                }}
              />
            </StyledRow>
          );
        })}
      </div>
    );
  };

  const renderLanguageFilter = (
    supportedLanguages?: api.PrevisitProvider.SupportedLanguageMap,
  ) => {
    if (!supportedLanguages || Object.keys(supportedLanguages).length === 0) {
      return null;
    }
    const supportedLanguagesList = Object.keys(supportedLanguages);
    const shownLanguages = showMoreLanguages
      ? supportedLanguagesList
      : supportedLanguagesList.slice(0, 5);
    const hiddenLanguagesCount =
      supportedLanguagesList.length - shownLanguages.length;
    return (
      <div>
        <RowWithPadding>
          <Typography variant="title-medium" bold className="ps-0">
            Languages
          </Typography>
        </RowWithPadding>
        {shownLanguages.map((lang) => {
          const checked = filterOptions.spokenLanguages?.includes(
            lang as api.Language.LanguageWithOther,
          );
          const count =
            supportedLanguages[lang as api.Language.LanguageWithOther];
          return (
            <StyledRow key={lang}>
              <Checkbox
                label={`${
                  api.Language.languageMap[
                    lang as api.Language.LanguageWithOther
                  ]
                } (${count ?? 1})`}
                small
                checked={checked ?? false}
                onChange={(event) => {
                  const updatedSpokenLanguages = updateFilterParams({
                    checked: event.target.checked,
                    embedded,
                    filterItem: lang,
                    organizationId,
                    paramString: 'spokenLanguage',
                    params,
                    pathname,
                  });
                  setFilterOptions({
                    ...filterOptions,
                    spokenLanguages: updatedSpokenLanguages.filter(
                      (
                        spokenLanguage,
                      ): spokenLanguage is api.Language.LanguageWithOther =>
                        isEnumMember(
                          api.Language.LanguageWithOther,
                          spokenLanguage,
                        ),
                    ),
                  });
                }}
              />
            </StyledRow>
          );
        })}
        <RowWithPadding className="pt-2">
          {!showMoreLanguages && !!hiddenLanguagesCount && (
            <ShowMoreButton
              variant="title-medium"
              bold
              className="ps-0"
              color="primary"
              onClick={() => {
                setShowMoreLanguages(true);
              }}
            >
              + {hiddenLanguagesCount} more
            </ShowMoreButton>
          )}
        </RowWithPadding>
      </div>
    );
  };

  const renderGenderFilter = (
    supportedSexes?: api.PrevisitProvider.SupportedSexMap,
  ) => {
    if (!supportedSexes || Object.keys(supportedSexes).length === 0) {
      return null;
    }
    return (
      <div>
        <RowWithPadding>
          <Typography variant="title-medium" bold className="ps-0">
            Gender
          </Typography>
        </RowWithPadding>
        {Object.keys(supportedSexes).map((sex) => {
          const checked = filterOptions.sexes?.includes(sex as api.User.Sex);
          const count = supportedSexes[sex as api.User.Sex];
          return (
            <StyledRow key={sex}>
              <Checkbox
                label={`${_.upperFirst(sex)} (${count})`}
                small
                checked={checked}
                onChange={(event) => {
                  const updatedSex = updateFilterParams({
                    checked: event.target.checked,
                    embedded,
                    filterItem: sex,
                    organizationId,
                    paramString: 'sex',
                    params,
                    pathname,
                  });
                  setFilterOptions({
                    ...filterOptions,
                    sexes: updatedSex.filter((sex): sex is api.User.Sex =>
                      isEnumMember(api.User.Sex, sex),
                    ),
                  });
                }}
              />
            </StyledRow>
          );
        })}
      </div>
    );
  };

  const renderInsuranceFilter = (acceptedPayorWithPlanTypes?: string[]) => {
    if (
      !acceptedPayorWithPlanTypes ||
      acceptedPayorWithPlanTypes.length === 0
    ) {
      return null;
    }
    return (
      <div>
        <Row>
          <Typography variant="title-medium" bold className="ps-0">
            Insurances accepted
          </Typography>
        </Row>
        <Row>
          <StyledTypeahead
            id="insurances-typeahead"
            options={acceptedPayorWithPlanTypes}
            multiple
            className="ps-0 pt-2"
            placeholder="Insurances"
            onChange={(selected) => {
              if (params) {
                params.delete('insurances');
                selected.forEach((insurance) =>
                  params.append('insurances', insurance as string),
                );
                updateWindowHistory({
                  pathname,
                  organizationId,
                  params,
                  embedded,
                });
                setFilterOptions({
                  ...filterOptions,
                  payorWithPlanTypes: params.getAll('insurances'),
                });
              }
            }}
            selected={filterOptions.payorWithPlanTypes}
          />
        </Row>
      </div>
    );
  };

  const updateFilterParam = (checked: boolean, paramString: string) => {
    if (params && checked) {
      params.append(paramString, 'true');
    } else if (params) {
      params.delete(paramString);
    }
    updateWindowHistory({
      pathname,
      organizationId,
      params,
      embedded,
    });
    setFilterOptions({
      ...filterOptions,
      [paramString]: checked ? true : undefined,
    });
    setCurrentPage(1);
  };

  return (
    <StyledCol
      lg="auto"
      className="d-none d-lg-block me-4 ps-0 pe-4 border-end"
    >
      <Offcanvas show={showFilters} onHide={handleClose} responsive="lg">
        <Offcanvas.Header className="border-bottom">
          <Offcanvas.Title>
            <Typography variant="title-large" bold>
              Filters
            </Typography>
          </Offcanvas.Title>
          <CloseButton onClick={handleClose} />
        </Offcanvas.Header>
        <Offcanvas.Body>
          <FluidFiltersContainer fluid>
            <div>
              <TopRowWithPadding>
                <Typography variant="title-medium" bold className="ps-0">
                  Suggested filters
                </Typography>
              </TopRowWithPadding>
              <StyledRow>
                <Checkbox
                  label="Primary Care Providers"
                  small
                  checked={filterOptions.isPrimaryCareProvider ?? false}
                  onChange={(event) => {
                    updateFilterParam(
                      event.target.checked,
                      'isPrimaryCareProvider',
                    );
                  }}
                />
              </StyledRow>
              <StyledRow>
                <Checkbox
                  label="Accepts new patients"
                  small
                  checked={filterOptions.acceptsNewPatients ?? false}
                  onChange={(event) => {
                    updateFilterParam(
                      event.target.checked,
                      'acceptsNewPatients',
                    );
                  }}
                />
              </StyledRow>
              <StyledRow>
                <Checkbox
                  label="Offers online scheduling"
                  small
                  checked={filterOptions.offersOnlineScheduling ?? false}
                  onChange={(event) => {
                    updateFilterParam(
                      event.target.checked,
                      'offersOnlineScheduling',
                    );
                  }}
                />
              </StyledRow>
              <StyledRow>
                <Checkbox
                  label="Offers virtual visits"
                  small
                  checked={filterOptions.offersVirtualVisits ?? false}
                  onChange={(event) => {
                    updateFilterParam(
                      event.target.checked,
                      'offersVirtualVisits',
                    );
                  }}
                />
              </StyledRow>
            </div>
            <AgesServedFilter
              embedded={embedded}
              filterOptions={filterOptions}
              organizationId={organizationId}
              params={params}
              pathname={pathname}
              setFilterOptions={setFilterOptions}
              setCurrentPage={setCurrentPage}
            />
            {renderInsuranceFilter(acceptedPayorWithPlanTypes)}
            {!!supportedLocations &&
            !!Object.keys(supportedLocations).length ? (
              <LocationsFilter
                embedded={embedded}
                filterOptions={filterOptions}
                organizationId={organizationId}
                params={params}
                pathname={pathname}
                setFilterOptions={setFilterOptions}
                setCurrentPage={setCurrentPage}
                supportedLocations={supportedLocations}
              />
            ) : (
              renderPracticeFilter(supportedPractices)
            )}
            {renderGenderFilter(supportedSexes)}
            {renderLanguageFilter(supportedLanguages)}
          </FluidFiltersContainer>
        </Offcanvas.Body>
      </Offcanvas>
    </StyledCol>
  );
};

export default Filters;

const StyledCol = styled(Col)`
  border-color: var(--color-border-default-light) !important;
  width: 250px;
  margin-bottom: auto;
`;

const StyledTypeahead = styled(Typeahead)`
  & button.close {
    background-color: transparent;
    border: 0;
  }
`;

const TopRowWithPadding = styled(Row)`
  padding-top: 8px;
  padding-bottom: 8px;

  @media (min-width: 992px) {
    padding-top: 24px;
  }
`;

const FluidFiltersContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CloseButton = styled(CloseIcon)`
  cursor: pointer;
`;

const ShowMoreButton = styled(Typography)`
  cursor: pointer;
`;
